import { BaseMutationOptions } from "@apollo/client";
import CloseIcon from "@assets/images/CloseModal.svg";
import RefreshIcon from "@assets/images/refresh-primary.svg";
import { Box, Button, Divider, Drawer } from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import { useUser } from "@saleor/auth";
import Checkbox from "@saleor/components/Checkbox";
import DialogProcessing from "@saleor/components/DialogProcessing";
import MyTypography from "@saleor/components/MyTypography";
import {
  AtpctpActionType,
  InquiryMethodType,
  PermissionEnum,
  useCheckoutAtpctpRequestMutationMutation
} from "@saleor/graphql";
import useBulkActions from "@saleor/hooks/useBulkActions";
import { renderCollection } from "@saleor/misc";
import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState
} from "react";
import SVG from "react-inlinesvg";
import { useIntl } from "react-intl";

import AtpCtpTableData from "./AtpCtpTable";
import useStyles from "./styles";

export interface AtpCtpDialogProps {
  onClose?: () => void;
  open: boolean;
  handleSubmit?: (value: BaseMutationOptions) => void;
  listAtpCtp: any[];
  title?: string;
  orderId?: any;
  isDisapearItem?: boolean;
  displayByOrderNo?: boolean;
  displayOrderNo?: boolean;
  refetchData?: () => void;
  setIsOpenProcessingAtpCtp?: Dispatch<SetStateAction<boolean>>;
  itemNoLatest?: number;
  loading?: boolean;
}

const AtpCtpDialog: React.FC<AtpCtpDialogProps> = props => {
  const {
    open,
    onClose,
    handleSubmit,
    listAtpCtp,
    title,
    orderId,
    isDisapearItem,
    displayByOrderNo,
    displayOrderNo,
    refetchData,
    setIsOpenProcessingAtpCtp
  } = props;
  const classes = useStyles();

  const intl = useIntl();
  const { user } = useUser();

  const [finalData, setFinaldata] = useState([]);
  const [isUpdated, setIsUpdated] = useState(false);

  const isCustomer = useMemo(
    () =>
      !!user.userPermissions.find(
        e => e.code === PermissionEnum.ALL_REPORT_CUSTOMER
      ),
    [user.userPermissions]
  );

  const converValueInquiryToEnum = (value: string): InquiryMethodType => {
    if (value === "Domestic") {
      return InquiryMethodType.DOMESTIC;
    }
    if (value === InquiryMethodType.ASAP) {
      return InquiryMethodType.ASAP;
    }
  };

  const getInquiryMethod = (method?: InquiryMethodType): InquiryMethodType => {
    if (method) {
      return converValueInquiryToEnum(method);
    }
    return isCustomer ? InquiryMethodType.CUSTOMER : InquiryMethodType.DOMESTIC;
  };

  const [
    getDataAtpCtp,
    { data: dataSearch, loading: loadingAtpCtp }
  ] = useCheckoutAtpctpRequestMutationMutation({
    // displayLoader: true,
    variables: {
      input: listAtpCtp.map(e => ({
        itemNo: e.itemNo,
        orderNo: e.orderNo,
        lineId: e.id,
        quantity: String(e.quantity),
        plant: e.plant,
        requestDate: e.requestDate,
        inquiryMethod: getInquiryMethod(e.inquiryMethod),
        consignmentLocation: e.consignmentLocation ? e.consignmentLocation : ""
      }))
    }
  });

  useEffect(() => {
    getDataAtpCtp();
    reset();
  }, [listAtpCtp]);

  const activeDataList = useMemo(
    () => [...finalData].filter(e => !e.disabled),
    [finalData]
  );

  const listAtpCtpData = useMemo(() => {
    const items = dataSearch?.checkoutAtpCtpRequest?.items || [];
    const itemsFailed = dataSearch?.checkoutAtpCtpRequest?.itemsFailed || [];
    const mappedItems = items.map(item => ({
      ...item,
      disabled: false
    }));
    const mappedItemsFailed = itemsFailed.map(item => ({
      ...item,
      disabled: true
    }));
    return [...mappedItems, ...mappedItemsFailed];
  }, [dataSearch?.checkoutAtpCtpRequest?.items]);

  useEffect(() => {
    if (
      dataSearch?.checkoutAtpCtpRequest &&
      !dataSearch?.checkoutAtpCtpRequest?.items
    ) {
      onClose();
    }
  }, [dataSearch]);

  const { isSelected, toggle, listElements, reset, set } = useBulkActions([]);

  const handleCheckboxAll = () => {
    if (listElements.length === activeDataList.length) {
      reset();
      return;
    }
    set(activeDataList.map(item => `${item.lineId}`));
  };

  useEffect(() => {
    setFinaldata(listAtpCtpData);
  }, [listAtpCtpData]);

  const disapearItem = () => {
    const newData = [...finalData].filter(e => {
      if (!listElements.includes(`${e.lineId}`)) {
        return e;
      }
    });
    setFinaldata(newData);
    set([]);
  };

  const handleSubmitUpdateOrderLine = (status: AtpctpActionType) => {
    if (listElements.length) {
      setIsOpenProcessingAtpCtp(true);
      handleSubmit({
        variables: {
          input: {
            items: listElements.map(e => {
              const orderline = finalData.find(ol => `${ol?.lineId}` === e);
              delete orderline.__typename;
              delete orderline.disabled;
              const listItems = orderline.listItems.map(item => {
                const { __typename, ...rest } = item;
                return rest;
              });
              return {
                ...orderline,
                listItems,
                action:
                  status === AtpctpActionType.COMMIT
                    ? AtpctpActionType.COMMIT
                    : AtpctpActionType.ROLLBACK
              };
            })
          }
        }
      });
      setIsUpdated(true);

      const isCloseModal = finalData
        .filter(item => !listElements.includes(item.lineId)) // กรองข้อมูลที่ไม่อยู่ใน listElements
        .every(item => item.disabled); // ตรวจสอบว่าทุก item ถูก disabled หรือไม่

      if (isCloseModal) {
        onClose();
      }
    }
  };

  useEffect(() => {
    if (isDisapearItem) {
      disapearItem();
    }
  }, [isDisapearItem]);

  const handleClose = () => {
    if (isUpdated && refetchData) {
      refetchData();
    }
    if (activeDataList.length > 0) {
      handleSubmit({
        variables: {
          input: {
            items: activeDataList.map(item => {
              delete item?.__typename;
              delete item?.disabled;
              const listItems = item.listItems.map(item => {
                const { __typename, ...rest } = item;
                delete item?.__typename;
                delete item?.disabled;
                return rest;
              });
              return {
                ...item,
                listItems,
                action: AtpctpActionType.ROLLBACK
              };
            })
          }
        }
      });
    }
    onClose();
  };

  return (
    <>
      <Drawer
        onClose={handleClose}
        open={open}
        anchor={"bottom"}
        PaperProps={{
          className: classes.wrapper
        }}
      >
        <div className={classes.titleWrapper}>
          <Box display={"flex"} alignItems="center">
            <MyTypography
              variant="h5"
              className={classes.titleModal}
              data-testid="title"
            >
              {title || "ATP/CTP Results"}
            </MyTypography>
            {orderId || displayByOrderNo ? (
              <Box ml={5}>
                <MyTypography variant="body1" data-testid="orderNoTitle">
                  Order No.{" "}
                  <b>
                    {orderId ||
                      dataSearch?.checkoutAtpCtpRequest?.items[0]?.orderNo}
                  </b>
                </MyTypography>
              </Box>
            ) : null}
          </Box>
          <div className={classes.headerClose}>
            <MyTypography variant={"caption"}>
              {intl.formatMessage({ defaultMessage: "ปิดหน้าต่าง" })}
            </MyTypography>
            <Button
              data-testid="closeButton"
              disabled={loadingAtpCtp}
              onClick={handleClose}
            >
              <img
                className={loadingAtpCtp ? classes.disabledBtn : null}
                src={CloseIcon}
                alt="Close"
              />
            </Button>
          </div>
        </div>
        <Box
          display={"flex"}
          alignItems="center"
          className={classes.rowCheckBox}
        >
          <Checkbox
            disabled={!activeDataList.length}
            indeterminate={
              listElements.length > 0 &&
              listElements.length < activeDataList.length
            }
            checked={listElements.length === activeDataList.length}
            onClick={handleCheckboxAll}
            size={"small"}
            className={classes.checkbox}
            data-testid="checkbox-all"
          />
          <MyTypography variant="body2">{`เลือก`}</MyTypography>
          <></>
          <MyTypography variant="subtitle2">
            &nbsp;{listElements.length}&nbsp;
          </MyTypography>
          <MyTypography variant="body2">{` รายการ`}</MyTypography>
        </Box>
        <Divider className={classes.divider} />
        <Box className={classes.tableWrapper}>
          {renderCollection(finalData, (item, index) => (
            <div key={index} className={classes.rowCheckBox}>
              <Checkbox
                size={"small"}
                checked={isSelected(`${item.lineId}`) && !item.disabled}
                onClick={() => toggle(`${item.lineId}`)}
                className={classes.checkbox}
                disabled={item.disabled}
                data-testid="checkbox-line"
              />
              <AtpCtpTableData
                displayOrderNo={displayOrderNo}
                onClickCheckBox={id => toggle(`${id}`)}
                selectedData={isSelected(`${item.lineId}`)}
                data={item}
              />
            </div>
          ))}
        </Box>
        <Box
          display={"flex"}
          width="calc(100% - 8rem)"
          justifyContent={"center"}
          position={"fixed"}
          bgcolor="#F4F7FE"
          pt={4}
          pb={4}
          alignItems="center"
          zIndex={10}
          bottom={0}
        >
          <Button
            data-testid="rollbackButton"
            className={classes.btnConfirm}
            color="secondary"
            variant="outlined"
            disabled={!listElements.length}
            onClick={() => {
              handleSubmitUpdateOrderLine(AtpctpActionType.ROLLBACK);
            }}
          >
            <SVG src={RefreshIcon} width={23.4} height={23.4} />
            <MyTypography
              style={{
                color: "inherit"
              }}
              variant="button2"
            >
              {intl.formatMessage({ defaultMessage: "ROLLBACK" })}
            </MyTypography>
          </Button>
          <Box mr={2} />
          <Button
            data-testid="acceptButton"
            className={classes.btnConfirm}
            color="secondary"
            variant="contained"
            disabled={!listElements.length}
            onClick={() => {
              handleSubmitUpdateOrderLine(AtpctpActionType.COMMIT);
            }}
          >
            <CheckIcon />
            <MyTypography variant="button2">
              {intl.formatMessage({ defaultMessage: "ACCEPT" })}
            </MyTypography>
          </Button>
        </Box>
      </Drawer>
      <DialogProcessing
        open={loadingAtpCtp}
        description={"ระบบกำลังประมวลผล กรุณารอสักครู่"}
      />
    </>
  );
};

export default AtpCtpDialog;
